import * as React from "react";
import { Clock } from "lucide-react";
import { Button, Progress } from "@/components/ui";
import { HoloShimmer } from "@/components/Holo";
import { DesignPackageDetail } from "@/services/bedrock/dtos/DesignPackage";
import { DesignPackageProgress } from "@/utils/actioncable";
import { useInterval } from "@/utils/useInterval";
import { LOADING_TIPS } from "../loadingTips";
import { percentFor, Stage, STAGES, stageForStatus } from "../progress";
import { StageStepper } from "./StageStepper";

export type ProgressSnapshot = DesignPackageProgress;

interface GenerationProgressProps {
  pkg: DesignPackageDetail;
  progress: ProgressSnapshot | undefined;
  canEdit: boolean;
  onBackToList(): void;
}

const TIP_INTERVAL_MS = 15_000;
const SLOW_AFTER_MS = 5 * 60_000;
const CLOCK_TICK_MS = 5_000;
/** A start time older than this is a stale `createdAt` (e.g. a retry), not this run's start. */
const MAX_TRUSTED_ELAPSED_MS = 60 * 60_000;

/** When this run started, or null when the package doesn't tell us. */
function runStartedAt(pkg: DesignPackageDetail, now: number): number | null {
  const started = Date.parse(pkg.inFlight?.kind === "generate" ? pkg.inFlight.startedAt : pkg.createdAt);
  if (Number.isNaN(started) || now - started > MAX_TRUSTED_ELAPSED_MS) return null;
  return started;
}

function formatElapsed(ms: number): string {
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return "started just now";
  return `started ${minutes} min ago`;
}

export const GenerationProgress: React.FC<GenerationProgressProps> = ({ pkg, progress, onBackToList }) => {
  const stage = stageForStatus(pkg.status);
  const current: Stage = typeof stage === "number" ? stage : 4;
  const packageProgress = progress?.designPackageItemId == null ? progress : undefined;
  const sublabel = packageProgress?.progressStatus ?? pkg.progressStatus ?? undefined;
  const percent = percentFor(pkg.status, packageProgress?.percent ?? pkg.percent);

  const tips = React.useMemo(
    () => LOADING_TIPS.filter(tip => tip.appliesTo.includes(pkg.contextType)),
    [pkg.contextType]
  );
  const [tipIndex, setTipIndex] = React.useState(0);
  useInterval(
    () => setTipIndex(i => (i + 1) % Math.max(tips.length, 1)),
    tips.length > 1 ? TIP_INTERVAL_MS : undefined
  );
  const tip = tips[tipIndex % Math.max(tips.length, 1)];

  const [now, setNow] = React.useState(() => Date.now());
  useInterval(() => setNow(Date.now()), CLOCK_TICK_MS);
  const mountedAt = React.useRef(now);
  const startedAt = runStartedAt(pkg, now);
  const elapsed = now - (startedAt ?? mountedAt.current);
  const slow = elapsed >= SLOW_AFTER_MS;

  return (
    <div
      className="mx-auto grid w-full max-w-4xl items-start gap-8 md:items-center md:grid-cols-[minmax(0,360px)_minmax(0,1fr)] md:gap-x-12"
      data-testid="generation-progress"
    >
      <div className="mx-auto w-full max-w-[360px]">
        <HoloShimmer aspectRatio={pkg.aspectRatio} />
      </div>

      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <h2 className="text-xl font-semibold text-slate-900">Building your design</h2>
          <p className="text-sm text-slate-500" data-testid="generation-eta">
            Usually 2–3 minutes{startedAt != null && ` · ${formatElapsed(elapsed)}`}
          </p>
          <p className="text-sm text-slate-500">
            Good design takes a little time. We're building a polished first draft so you have less to tweak.
          </p>
        </div>

        <div className="flex flex-col gap-4">
          <p className="sr-only" role="status">
            {STAGES[current - 1].label}
            {sublabel && `: ${sublabel}`}
          </p>
          <StageStepper steps={STAGES} current={current} sublabel={sublabel} />
          <Progress value={percent} className="max-w-sm" aria-label="Progress" />
        </div>
      </div>

      <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6 md:col-span-2">
        <div className="flex items-start gap-3">
          <Clock className="mt-0.5 size-5 shrink-0 text-slate-500" aria-hidden="true" />
          <div className="flex flex-col gap-1 text-sm">
            <p className="font-semibold text-slate-900">No need to wait here</p>
            <p className="text-slate-600">
              Your design keeps building after you leave. It'll be in Designs when it's ready.
            </p>
            <div aria-live="polite">
              {slow && (
                <p className="font-medium text-slate-700">Still working. Some designs take a few extra minutes.</p>
              )}
            </div>
          </div>
        </div>
        <Button className="self-start shrink-0 sm:self-center" onClick={onBackToList}>
          Back to designs
        </Button>
      </div>

      {tip && (
        <p
          className="flex items-baseline justify-center gap-2 text-center text-sm text-slate-500 md:col-span-2"
          data-testid="loading-tip"
        >
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Tip</span>
          <span key={tipIndex} className="animate-in fade-in duration-500 motion-reduce:animate-none">
            {tip.text}
          </span>
        </p>
      )}
    </div>
  );
};
